import {
  cloneElement,
  isValidElement,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { css, generateUtilityClasses, styled } from '@mui/material';
import { np, useTranslation } from '@kc-console/core';
import { IcSearchRegular } from '@kep/kcds-icons';

import { useCascadeRef, useCascadeState } from '../../utils';
import { MenuContainerToolbar } from '../MenuContainerToolbar';
import { MENU_CONTAINER_TOOLBAR_SELECT_ALL_KEY } from '../MenuContainerToolbar/MenuContainerToolbar';
import { MenuList, useMenuListNavigation } from '../MenuList';
import { getMenuListOptionId, isMenuListOptionKey } from '../MenuList/utils';
import { TextInput } from '../TextInput';

import type { ChangeEvent, KeyboardEvent, ReactElement } from 'react';
import type { MenuContainerToolbarProps } from '../MenuContainerToolbar';
import type { MenuContainerApiRef, MenuContainerColor, MenuContainerProps } from './types';

// ============================================================================
// Classes
// ============================================================================

export const menuContainerClasses = generateUtilityClasses('MenuContainer', [
  'root',
  'header',
  'menuList',
  'footer',
]);

// ============================================================================
// Main Component
// ============================================================================

export default function MenuContainer(props: MenuContainerProps) {
  const { t } = useTranslation();
  const {
    apiRef,
    autoFocusItem = false,
    autoFocusSearchInput = false,
    className,
    color = 'default',
    disableTooltipOnEllipsis = false,
    ellipsisTooltipPlacement = 'right',
    enableHighlight = true,
    expandable,
    filterItems,
    footer,
    header,
    items,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    id,
    listRef: listRefProp,
    noFilteredOptionsText = t('common:common.list.no_matching_items'),
    noOptionsText = t('common:common.list.no_selectable_items'),
    onKeyDown,
    onSearchTextChange,
    ref,
    renderContent,
    role,
    searchable = false,
    searchPlaceholder = t('common:common.general.enter_search_term'),
    searchText: searchTextProp,
    selectedType = 'check',
    slotProps,
    sx,
    tabIndex = -1,
  } = props;
  const menuListSlotProps = slotProps?.menuList;

  const rootRef = useCascadeRef(null, ref);
  const listRef = useCascadeRef<HTMLUListElement | null>(null, listRefProp);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // ========== 검색 상태 관리 (controlled/uncontrolled) ==========
  const [searchValue, setSearchValue] = useCascadeState({
    initialValue: '',
    value: searchTextProp,
    onChange: onSearchTextChange,
  });

  // ========== header에 MenuContainerToolbar 관련 예약 로직 ==========
  const hasHeaderAsToolbar = isValidElement(header) && header.type === MenuContainerToolbar;

  // ========== Section 접기/펴기 상태 ==========
  const [sectionFolded, setSectionFolded] = useState(new Set<string>());
  const handleToggleSectionFolded = (key: string) => {
    setSectionFolded(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // ========== 가상 포커스 관리 (useMenuListNavigation 사용) ==========
  const navigation = useMenuListNavigation({
    items,
    expandable,
    filterItems,
    searchText: searchValue,
    sectionFolded,
    prependFocusableKeys: hasHeaderAsToolbar ? [MENU_CONTAINER_TOOLBAR_SELECT_ALL_KEY] : undefined,
  });

  const { focusedItemKey, isItemFocused } = navigation;
  const navigationContainerProps = navigation.getContainerProps({ ref: rootRef });
  const semanticListboxId =
    menuListSlotProps?.role === 'listbox' ? menuListSlotProps.id : undefined;
  const hasSemanticListbox = !!semanticListboxId;
  const activeDescendantId =
    hasSemanticListbox && focusedItemKey && isMenuListOptionKey(focusedItemKey)
      ? getMenuListOptionId(semanticListboxId, focusedItemKey)
      : undefined;

  // ========== 검색 입력 핸들러 ==========
  const handleSearchInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setSearchValue(e.target.value);
    },
    [setSearchValue],
  );

  const handleSearchInputClear = useCallback(() => {
    setSearchValue('');
  }, [setSearchValue]);

  const handleSearchInputKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        navigation.navigateDown();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        navigation.navigateUp();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        navigation.selectFocused({ keepVirtualFocus: true });
      }
    },
    [navigation],
  );

  // ========== 외부 제어 API (ref를 통해 노출) ==========
  useImperativeHandle<MenuContainerApiRef, MenuContainerApiRef>(
    apiRef,
    () => ({
      get focusedItemKey() {
        return focusedItemKey;
      },
      navigateUp: navigation.navigateUp,
      navigateDown: navigation.navigateDown,
      selectFocused: navigation.selectFocused,
      setFocusedKey: navigation.setFocusedKey,
      focusTrigger: () => {
        if (searchable) {
          searchInputRef.current?.focus();
          return;
        }

        listRef.current?.focus();
      },
    }),
    [focusedItemKey, listRef, navigation, searchable],
  );

  // ========== Render ==========
  return (
    <MenuContainerRoot
      {...navigationContainerProps}
      aria-activedescendant={
        hasSemanticListbox ? undefined : navigationContainerProps['aria-activedescendant']
      }
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      className={className}
      color={color}
      hasFooter={!!footer}
      id={id}
      onKeyDown={onKeyDown}
      role={role ?? navigationContainerProps.role}
      sx={sx}
      tabIndex={tabIndex}
    >
      {/* Header / Search */}
      {(header || searchable) && (
        <MenuContainerHeader>
          {searchable && (
            <MenuContainerSearchInputContainer>
              <MenuContainerSearchInput
                autoFocus={autoFocusSearchInput ?? autoFocusItem}
                inputRef={searchInputRef}
                size="lg"
                placeholder={searchPlaceholder}
                startAdornment={<IcSearchRegular />}
                value={searchValue}
                onChange={handleSearchInputChange}
                onClear={handleSearchInputClear}
                onKeyDown={handleSearchInputKeyDown}
                slotProps={{
                  input: {
                    'aria-activedescendant': activeDescendantId,
                    'aria-controls': hasSemanticListbox ? semanticListboxId : undefined,
                    'aria-label': hasSemanticListbox ? searchPlaceholder : undefined,
                  },
                }}
                {...navigation.getTriggerProps()}
              />
            </MenuContainerSearchInputContainer>
          )}
          {hasHeaderAsToolbar
            ? cloneElement(
                header as ReactElement<MenuContainerToolbarProps, typeof MenuContainerToolbar>,
                {
                  isSearched: !!searchValue,
                  isSelectAllCheckboxFocused: isItemFocused(MENU_CONTAINER_TOOLBAR_SELECT_ALL_KEY),
                },
              )
            : header}
        </MenuContainerHeader>
      )}

      <MenuContainerMenuList
        autoFocusItem={autoFocusItem ? !searchable : false}
        color={color}
        disableTooltipOnEllipsis={disableTooltipOnEllipsis}
        ellipsisTooltipPlacement={ellipsisTooltipPlacement}
        enableHighlight={enableHighlight}
        expandable={expandable}
        filterItems={filterItems}
        getItemProps={navigation.getItemProps}
        hasHeader={!!(header || searchable)}
        isItemFocused={isItemFocused}
        items={items}
        noFilteredOptionsText={noFilteredOptionsText}
        noOptionsText={noOptionsText}
        onItemClick={navigation.handleItemClick}
        onKeyDown={navigation.handleKeyDown}
        onToggleSectionFolded={handleToggleSectionFolded}
        ref={listRef}
        renderContent={renderContent}
        searchText={searchValue}
        sectionFolded={sectionFolded}
        selectedType={selectedType}
        tabIndex={0}
        {...navigation.getTriggerProps()}
        {...menuListSlotProps}
        aria-activedescendant={activeDescendantId}
      />

      {/* Footer */}
      {footer && <MenuContainerFooter>{footer}</MenuContainerFooter>}
    </MenuContainerRoot>
  );
}

// ============================================================================
// Styled Components
// ============================================================================

const MenuContainerRoot = styled('div', {
  target: menuContainerClasses.root,
  shouldForwardProp: np('color', 'hasFooter'),
})<{
  color: MenuContainerColor;
  hasFooter?: boolean;
}>`
  display: flex;
  flex-direction: column;

  ${({ theme, color, hasFooter }) =>
    hasFooter &&
    css`
      & .${menuContainerClasses.menuList} {
        border-bottom: ${theme.mixins.border(
          color !== 'global' ? 'border/weaker/default' : 'border/global/default',
        )};
      }
    `};
`;

const MenuContainerHeader = styled('div', {
  target: menuContainerClasses.header,
})`
  padding: 8px 16px 0px;
`;

const MenuContainerSearchInputContainer = styled('div')`
  padding: 8px 0px 12px;
`;

const MenuContainerFooter = styled('div', {
  target: menuContainerClasses.footer,
})`
  padding: 16px 16px;
`;

const MenuContainerMenuList = styled(MenuList, {
  target: menuContainerClasses.menuList,
  shouldForwardProp: np('hasHeader'),
})<{ hasHeader?: boolean }>(
  ({ hasHeader }) => css`
    padding-top: ${hasHeader ? 0 : 8}px;
    padding-bottom: 8px;
  `,
);

const MenuContainerSearchInput = styled(TextInput)`
  width: 100%;
`;
